import React from 'react'
import { FiShare2 } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { ConvertorMiladi } from 'utilities/helper/digitToMonth'
import { faPlain, toman } from 'utilities/helper/format'

interface ISubHeaderProps {
    data: ISubHeaderLoan
    setIsOpenLink: React.Dispatch<React.SetStateAction<boolean>>
}

/**
 * The loan's own facts: what was lent, when, and on what terms.
 *
 * Progress used to live here too — «اقساط پرداخت شده / باقیمانده / معوق» as
 * three gray rows — but the card under it now answers that with the remaining
 * balance and a bar, and two answers to one question is worse than one. (The
 * «باقیمانده» row was also reading `number_of_repayments`, the loan's TOTAL
 * installment count, so it was the wrong number besides.)
 *
 * The two full-width green blocks are gone for the same reason: neither is the
 * page's action. The page's action is paying, and it lives in the basket bar.
 */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-text-secondary shrink-0">
                {label}
            </span>
            <span className="text-[15px] font-medium text-text-body truncate">
                {value}
            </span>
        </div>
    )
}

export default function SubHeader({ data, setIsOpenLink }: ISubHeaderProps) {
    const navigate = useNavigate()
    const { moneyPool } = useParams()
    const interest = Number(data.interest_rate) * 100

    // This used to be navigate(-1). "Back" is not a destination: opening the
    // loan from the fund's وام‌ها tab (or from a shared link) sent «اطلاعات
    // عضو» to the fund page, or out of the app entirely. The member's own
    // page is a route we can name, so the button is named after it.
    //
    // The route still says financial-history; the page it lands on does
    // not. Since pwa#61 that route resolves to کارنامهٔ عضو for a manager
    // and for the borrower themselves — between them, nearly everyone who
    // opens a loan. It is still «سابقه مالی عضو» for the remaining case, a
    // plain member reading somebody else's loan; naming the button after
    // the common destination beats naming it after the rare one.
    const openMemberPage = () =>
        navigate(`/fund/financial-history/${moneyPool}/${data.poolship_id}`)

    return (
        <div className="px-4 pt-3">
            <div className="bg-white border border-divider rounded-card shadow-card p-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs text-text-secondary">مبلغ وام</p>
                        <p className="mt-[2px] text-[22px] font-bold text-heading leading-tight">
                            {toman(data.amount)}
                        </p>
                    </div>
                    <button
                        type="button"
                        aria-label="اشتراک‌گذاری لینک پرداخت اقساط"
                        onClick={() => setIsOpenLink(true)}
                        className="shrink-0 rounded-full border border-divider bg-sunken p-2 text-text-secondary active:scale-90"
                    >
                        <FiShare2 className="h-5 w-5" />
                    </button>
                </div>

                <div className="mt-3 flex flex-col gap-2 border-t border-divider pt-3">
                    <Row
                        label="تاریخ اعطا"
                        value={data.pay_date && ConvertorMiladi(data.pay_date)}
                    />
                    <Row
                        label="کارمزد"
                        value={
                            data.interest_pay_mode === 'lip_ma'
                                ? `افزودن به هر قسط (${faPlain.format(
                                      interest
                                  )} درصد)`
                                : 'کسر از مبلغ وام'
                        }
                    />
                </div>

                <button
                    type="button"
                    onClick={openMemberPage}
                    className="mt-3 w-full rounded-full border border-stroke bg-white py-2 text-sm font-bold text-text-body active:scale-[0.99] transition-transform motion-reduce:transition-none"
                >
                    کارنامهٔ عضو
                </button>
            </div>
        </div>
    )
}
